import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { AGENTS, AGENT_IDS } from '../agents.js';
import AgentMark from '../components/AgentMark.jsx';
import AskCard from '../components/AskCard.jsx';
import ActivityRow from '../components/ActivityRow.jsx';
import {
  fetchQueue,
  answerAsk, undoAnswer, toAskView,
} from '../desk-model.js';
import {
  fetchPending, fetchDisputes,
  approveEntry, rejectEntry, ruleDispute,
  fetchRoster, fetchRecommendation, nextRecommendation,
  tickAutopilot,
} from '../room-model.js';
import RecommendationCard from '../components/RecommendationCard.jsx';
import AutopilotStrip from '../components/AutopilotStrip.jsx';

// relTime — short relative string from a timestamp, for the status line.
function relTime(dateVal) {
  if (!dateVal) return 'a while ago';
  const d = new Date(dateVal).getTime();
  if (Number.isNaN(d)) return 'a while ago';
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) { const m = Math.floor(diff / 60); return m + ' minute' + (m === 1 ? '' : 's') + ' ago'; }
  if (diff < 86400) { const h = Math.floor(diff / 3600); return h + ' hour' + (h === 1 ? '' : 's') + ' ago'; }
  const days = Math.floor(diff / 86400);
  return days + ' day' + (days === 1 ? '' : 's') + ' ago';
}

// trafficWhen — a compact label for a traffic intercept's date (already a string
// label from the backend in most cases; pass strings through, format dates).
function trafficWhen(date) {
  if (!date) return '';
  if (typeof date === 'string' && !/^\d{4}-\d{2}-\d{2}T/.test(date)) return date;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return String(date);
  return relTime(date).toUpperCase();
}

export default function Desk({ projectId, onOpenThread, onChanged, chatter, onToggleChatter }) {
  const [project, setProject] = useState(null);
  const [queue, setQueue] = useState({ open: [], answered: [] });
  const [pending, setPending] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [threads, setThreads] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [roster, setRoster] = useState([]);
  const [recommendation, setRecommendation] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busyCard, setBusyCard] = useState(null);
  const [actionErr, setActionErr] = useState('');
  const alive = useRef(true);

  useEffect(() => () => { alive.current = false; }, []);

  // reload — refetch the parts that move after an answer/undo/approve/rule.
  const reload = useCallback(async () => {
    const [q, p, d, ro, rec] = await Promise.all([
      fetchQueue(projectId).catch(() => null),
      fetchPending(projectId).catch(() => null),
      fetchDisputes(projectId).catch(() => null),
      fetchRoster(projectId).catch(() => null),
      fetchRecommendation(projectId).catch(() => null),
    ]);
    if (!alive.current) return;
    setQueue((q && Array.isArray(q.open)) ? q : { open: [], answered: [] });
    setPending((p && Array.isArray(p.entries)) ? p.entries : []);
    setDisputes((d && Array.isArray(d.disputes)) ? d.disputes : []);
    setRoster((ro && Array.isArray(ro.roster)) ? ro.roster : []);
    setRecommendation((rec && rec.open) || null);
    // Notify the shell so the topbar badge (open count) stays honest.
    if (onChanged) onChanged();
  }, [projectId, onChanged]);

  useEffect(() => {
    let ok = true;
    Promise.all([
      api('/api/projects/' + projectId),
      fetchQueue(projectId),
      fetchPending(projectId).catch(() => null),
      fetchDisputes(projectId).catch(() => null),
      api('/api/projects/' + projectId + '/threads'),
      api('/api/projects/' + projectId + '/traffic'),
      fetchRoster(projectId).catch(() => null),
      fetchRecommendation(projectId).catch(() => null),
    ]).then(([proj, q, p, d, th, tr, ro, rec]) => {
      if (!ok || !alive.current) return;
      setProject(proj && proj._id ? proj : null);
      setQueue((q && Array.isArray(q.open)) ? q : { open: [], answered: [] });
      setPending((p && Array.isArray(p.entries)) ? p.entries : []);
      setDisputes((d && Array.isArray(d.disputes)) ? d.disputes : []);
      setThreads(Array.isArray(th) ? th : []);
      setTraffic(Array.isArray(tr) ? tr : []);
      setRoster((ro && Array.isArray(ro.roster)) ? ro.roster : []);
      setRecommendation((rec && rec.open) || null);
      setLoaded(true);
    }).catch(() => { if (ok && alive.current) setLoaded(true); });
    return () => { ok = false; };
  }, [projectId]);

  // Fire a Showrunner autopilot tick on Desk load as a natural reactive beat.
  useEffect(() => {
    tickAutopilot(projectId).catch(() => {});
  }, [projectId]);

  // While freshly seeded (no asks, no proposals, no disputes, no threads yet,
  // and no Showrunner recommendation yet), poll until the first item lands,
  // then the normal queue view takes over.
  const seeded = loaded && queue.open.length === 0 && queue.answered.length === 0
    && pending.length === 0 && disputes.length === 0 && threads.length === 0
    && roster.length === 0 && !recommendation;
  useEffect(() => {
    if (!loaded || !seeded) return;
    const id = setInterval(() => {
      reload();
      api('/api/projects/' + projectId + '/threads')
        .then(th => { if (alive.current && th) setThreads(th); })
        .catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [loaded, seeded, projectId, reload]);

  function handleAnswer(turnId, payload) {
    answerAsk(projectId, turnId, payload, (evt) => {
      // Reconcile the optimistic AskCard against server truth on completion OR
      // a stream-level error, so a failed answer never sits stuck.
      if (evt && (evt.type === 'done' || evt.type === 'error')) reload();
    }).catch(() => { reload(); });
  }

  async function handleUndo(turnId) {
    try { await undoAnswer(projectId, turnId); } finally { reload(); }
  }

  async function handleApprove(entry) {
    setBusyCard(entry._id); setActionErr('');
    try { await approveEntry(projectId, entry._id); }
    catch (e) { if (alive.current) setActionErr('That proposal could not be actioned, it may have already been handled. Refreshing.'); }
    finally { await reload(); if (alive.current) setBusyCard(null); }
    if (onChanged) onChanged();
  }

  async function handleReject(entry) {
    const note = window.prompt('Why send it back? (optional)') || '';
    setBusyCard(entry._id); setActionErr('');
    try { await rejectEntry(projectId, entry._id, note); }
    catch (e) { if (alive.current) setActionErr('That proposal could not be actioned, it may have already been handled. Refreshing.'); }
    finally { await reload(); if (alive.current) setBusyCard(null); }
    if (onChanged) onChanged();
  }

  async function handleRule(dispute, outcome) {
    const note = window.prompt('Add a note for the room? (optional)') || '';
    setBusyCard(dispute._id);
    try { await ruleDispute(projectId, dispute._id, outcome, note); } finally { await reload(); if (alive.current) setBusyCard(null); }
    if (onChanged) onChanged();
  }

  if (!loaded) return null;

  const openCount = queue.open.length;
  const totalNeedsYou = queue.open.length + pending.length + disputes.length;
  const workingCount = threads.length;
  const lastVisit = relTime(project && project.lastTouched);
  // The founding seed, restated persistently under the title.
  const seedText = project && ((project.seed && project.seed.text) || project.logline || '');

  const digestItems = traffic.map((t, i) => ({
    id: t._id || ('tr-' + i),
    kind: 'intercept',
    agent: t.from,
    toAgent: t.to,
    when: trafficWhen(t.date),
    text: t.body,
  }));
  const showDigest = chatter && digestItems.length > 0;
  const liveItems = digestItems.slice(0, 4);

  return (
    <div className="desk">
      <main>
        <div className="desk-head">
          <div className="dh-meta">
            <span className="mono-label">PROJECT{seeded ? ' · JUST SEEDED' : ''}</span>
          </div>
          <h1>{project ? project.title : 'The desk'}</h1>
          {seedText && <div className="seed-restate">"{seedText}"</div>}
          <div className="dh-line">
            {seeded ? (
              <>The room is taking its first pass on your seed.</>
            ) : (
              <>
                <span className="em">
                  {totalNeedsYou === 0 ? 'Nothing needs you' : totalNeedsYou + (totalNeedsYou === 1 ? ' item needs you' : ' items need you')}
                </span>
                {' · '}{workingCount} agent{workingCount === 1 ? '' : 's'} working · last visit {lastVisit}.
              </>
            )}
          </div>
        </div>

        {seeded ? (
          <>
            <div className="seeded-note">
              <div className="sn-h">
                <span className="pulse-dot" />
                <span className="sn-t">Nothing needs you yet. Seeds take a moment.</span>
              </div>
              <p>This desk stays quiet until an agent hits a fork it can't take for you. When that happens, the question arrives as a card right here, with what they need, why it matters, and answers you can give in one click.</p>
            </div>

            <div className="queue-h">
              <h2>First pass, underway</h2>
              <span className="q-sub">What each agent is doing with your seed right now.</span>
            </div>
            <div className="firstpass">
              {AGENT_IDS.map(id => {
                const a = AGENTS[id];
                return (
                  <div className="fp-row" key={id}>
                    <AgentMark agent={a} size="xs" />
                    <span className="fp-doing">{a.name} is exploring {a.role.toLowerCase()}.</span>
                    <span className="pulse-dot" />
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="queue-h">
              <h2>Needs you</h2>
              <span className="q-count">{totalNeedsYou} OPEN</span>
              <span className="q-sub">Answer here. Nothing else is waiting on you.</span>
            </div>

            {totalNeedsYou === 0 && queue.answered.length === 0 && (
              <div className="queue-empty">
                <div className="big">You're all caught up.</div>
                Every call answered. Agents are drafting against your decisions.
              </div>
            )}

            <div className="roster-strip">
              <div className="rs-h"><span className="mono-label">THE ROOM</span>
                <button type="button" className="thread-link" style={{ marginLeft: 'auto' }} onClick={async () => { await nextRecommendation(projectId).catch(() => {}); reload(); }}>Ask the Showrunner what's next</button>
              </div>
              <div className="rs-agents">
                {AGENT_IDS.map(id => {
                  const a = AGENTS[id];
                  const entry = (roster || []).find(r => r.agent === id);
                  const state = entry ? entry.state : 'dormant';
                  return (
                    <div className={`rs-agent ${state}`} key={id} title={`${a.name}: ${state}`}>
                      <AgentMark agent={a} size="xs" /><span className="rs-dot" /><span className="rs-name">{a.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <AutopilotStrip projectId={projectId} />
            {recommendation && (
              <RecommendationCard projectId={projectId} rec={recommendation} onActivated={() => reload()} onDismissed={() => reload()} />
            )}

            <div className="needs-grid">
              {/* LEFT: Questions */}
              <div className="needs-col">
                <div className="col-h">
                  <h3>Questions</h3>
                  <span className="col-count">{queue.open.length} OPEN</span>
                </div>
                {queue.open.length === 0 && queue.answered.length === 0 ? null : (
                  queue.open.length === 0 ? (
                    <div className="col-empty">No open questions. The room is proposing.</div>
                  ) : null
                )}
                {queue.open.map(item => {
                  const view = toAskView(item);
                  return (
                    <AskCard
                      key={view.turnId}
                      ask={view}
                      onAnswer={handleAnswer}
                      onUndo={handleUndo}
                      onOpenThread={onOpenThread}
                    />
                  );
                })}
                {queue.answered.map(item => {
                  const view = toAskView(item, { answered: true });
                  return (
                    <AskCard
                      key={view.turnId}
                      ask={view}
                      onAnswer={handleAnswer}
                      onUndo={handleUndo}
                      onOpenThread={onOpenThread}
                    />
                  );
                })}
              </div>

              {/* RIGHT: Proposals grouped by section */}
              <div className="needs-col">
                <div className="col-h">
                  <h3>Proposals</h3>
                  <span className="col-count">{pending.length}</span>
                </div>
                {actionErr && <div className="action-err">{actionErr}</div>}
                {pending.length === 0 ? (
                  <div className="col-empty">No pending proposals.</div>
                ) : (() => {
                  // Group by section
                  const grouped = [];
                  const seen = new Map();
                  for (const entry of pending) {
                    const sec = entry.section || '';
                    if (!seen.has(sec)) { seen.set(sec, []); grouped.push(sec); }
                    seen.get(sec).push(entry);
                  }
                  return grouped.map(sec => (
                    <div className="prop-group" key={sec || '__none__'}>
                      {sec && (
                        <p className="prop-group-h">
                          {sec.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                      )}
                      {seen.get(sec).map(entry => {
                        const a = AGENTS[entry.agent];
                        const acting = busyCard === entry._id;
                        return (
                          <div className="pending-card" key={entry._id}>
                            <div className="pc-tag">
                              <span className="mono-label">PENDING</span>
                              <AgentMark agent={a} size="xs" />
                              <span className="pc-agent">{(a ? a.name : entry.agent).toUpperCase()} proposes</span>
                              {entry.section && <span className="pc-section">{entry.section}</span>}
                            </div>
                            <p className="pc-body">{entry.prose}</p>
                            <div className="pending-actions">
                              <button type="button" className="btn small accent" disabled={acting} onClick={() => handleApprove(entry)}>Approve</button>
                              <button type="button" className="btn small ghost" disabled={acting} onClick={() => handleReject(entry)}>Reject</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>

            {disputes.length > 0 && (
              <div className="queue-group">
                <div className="queue-h">
                  <h2>Disputes to rule</h2>
                  <span className="q-count">{disputes.length}</span>
                  <span className="q-sub">Rule on each challenge or reopen request below.</span>
                </div>
                {disputes.map(d => {
                  const a = AGENTS[d.agent];
                  const isChallenge = d.kind === 'challenge';
                  const acting = busyCard === d._id;
                  return (
                    <div className="dispute-card" key={d._id}>
                      <div className="dc-tag">
                        <span className={`mono-label ${isChallenge ? 'challenge' : 'reopen'}`}>{isChallenge ? 'CHALLENGE' : 'REOPEN'}</span>
                        <AgentMark agent={a} size="xs" />
                        <span className="dc-agent">{(a ? a.name : d.agent).toUpperCase()}</span>
                      </div>
                      {d.target && <div className="dc-target">On: {d.target.prose}</div>}
                      <p className="dc-arg">{d.argument}</p>
                      <div className="pending-actions">
                        {isChallenge ? (
                          <>
                            <button type="button" className="btn small accent" disabled={acting} onClick={() => handleRule(d, 'won')}>Strike the entry</button>
                            <button type="button" className="btn small ghost" disabled={acting} onClick={() => handleRule(d, 'lost')}>Uphold the entry</button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="btn small accent" disabled={acting} onClick={() => handleRule(d, 'granted')}>Grant reopen</button>
                            <button type="button" className="btn small ghost" disabled={acting} onClick={() => handleRule(d, 'denied')}>Deny</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {digestItems.length > 0 && (
              <div className="digest">
                <div className="digest-h">
                  <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>While you were away</h2>
                  <button
                    type="button"
                    className="thread-link"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => onToggleChatter && onToggleChatter(!chatter)}
                  >
                    {chatter ? 'Hide agent chatter' : 'Show agent chatter'}
                  </button>
                </div>
                {showDigest
                  ? digestItems.map(item => <ActivityRow key={item.id} item={item} />)
                  : <div style={{ fontSize: 11.5, color: 'var(--ink-4)', paddingTop: 8 }}>Agent chatter hidden. Turn it on to see intercepts.</div>}
              </div>
            )}
          </>
        )}
      </main>

      <aside className="rail">
        {liveItems.length > 0 && (
          <div className="rail-block">
            <div className="rail-h"><span className="mono-label">THE ROOM · LIVE</span></div>
            {chatter
              ? liveItems.map(item => <ActivityRow key={item.id} item={item} />)
              : <div style={{ fontSize: 11.5, color: 'var(--ink-4)', paddingTop: 8 }}>Agent chatter hidden. Turn it on to see intercepts.</div>}
          </div>
        )}

        {threads.length > 0 && (
          <div className="rail-block">
            <div className="rail-h"><span className="mono-label">THREADS · THE STEERING WHEEL</span></div>
            {threads.map(t => {
              const a = AGENTS[t.agent];
              return (
                <button key={t._id} type="button" className="mini-ask" onClick={() => onOpenThread && onOpenThread(t._id)}>
                  <div className="ma-head"><AgentMark agent={a} size="xs" />{a && <span className="aname">{a.name.toUpperCase()}</span>}</div>
                  <div className="ma-q" style={{ color: 'var(--ink-3)', fontSize: 12 }}>{a ? a.role : t.title}</div>
                </button>
              );
            })}
          </div>
        )}
      </aside>
    </div>
  );
}
